import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { authApi } from '../../services/api/authApi';

interface TwoFactorSetupDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const steps = ['Scan QR Code', 'Verify Code', 'Save Backup Codes'];

export const TwoFactorSetupDialog: React.FC<TwoFactorSetupDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Setup
  const [secret, setSecret] = useState('');
  const [qrCode, setQrCode] = useState('');

  // Step 2: Verification
  const [verificationCode, setVerificationCode] = useState('');

  // Step 3: Backup codes
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [downloadedBackupCodes, setDownloadedBackupCodes] = useState(false);

  // Initialize setup when dialog opens
  useEffect(() => {
    if (open && activeStep === 0 && !secret) {
      initializeSetup();
    }
  }, [open]);

  const initializeSetup = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await authApi.setup2FA();
      setSecret(data.secret);
      setQrCode(data.qrCode);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to initialize 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode?.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await authApi.verifySetup2FA({
        code: verificationCode,
        secret: secret,
      });

      setBackupCodes(data.backupCodes);
      setActiveStep(2);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBackupCodes = () => {
    const content = backupCodes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'carboncare-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
    setDownloadedBackupCodes(true);
  };

  const handleComplete = () => {
    onSuccess();
    handleClose();
  };

  const handleClose = () => {
    // Reset state
    setActiveStep(0);
    setSecret('');
    setQrCode('');
    setVerificationCode('');
    setBackupCodes([]);
    setDownloadedBackupCodes(false);
    setError(null);
    onClose();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1" gutterBottom>
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </Typography>

            {(() => {
              if (loading) {
                return (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
                );
              }
              if (qrCode) {
                return (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    my: 3,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <img
                      src={qrCode}
                      alt="2FA QR Code"
                      style={{ width: 200, height: 200 }}
                    />
                  </Paper>
                </Box>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Or enter this code manually:
                </Typography>
                <Paper
                  sx={{
                    p: 1.5,
                    mt: 1,
                    backgroundColor: 'grey.100',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    wordBreak: 'break-all',
                  }}
                >
                  {secret}
                </Paper>
              </>
                );
              }
              return null;
            })()}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="body1" gutterBottom>
              Enter the 6-digit code from your authenticator app to verify setup
            </Typography>

            <TextField
              fullWidth
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => {
                const value = e.target.value.replaceAll(/\D/g, '').slice(0, 6);
                setVerificationCode(value);
              }}
              placeholder="000000"
              inputProps={{
                maxLength: 6,
                style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' },
              }}
              sx={{ mt: 2 }}
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Save these backup codes in a safe place. Each code can only be used once to access your account if you lose access to your authenticator app.
            </Alert>

            <Paper
              sx={{
                p: 2,
                backgroundColor: 'grey.50',
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              {backupCodes.map((code, index) => (
                <Typography
                  key={code}
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    py: 0.5,
                  }}
                >
                  {index + 1}. {code}
                </Typography>
              ))}
            </Paper>

            <Button
              fullWidth
              variant="outlined"
              onClick={handleDownloadBackupCodes}
              sx={{ mt: 2 }}
            >
              Download Backup Codes
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  const getActionButtons = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <Button onClick={handleClose} variant="outlined">Cancel</Button>
            <Button
              variant="contained"
              onClick={() => setActiveStep(1)}
              disabled={!qrCode || loading}
            >
              Next
            </Button>
          </>
        );

      case 1:
        return (
          <>
            <Button onClick={() => setActiveStep(0)}>Back</Button>
            <Button
              variant="contained"
              onClick={handleVerifyCode}
              disabled={verificationCode.length !== 6 || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify'}
            </Button>
          </>
        );

      case 2:
        return (
          <Button
              variant="contained"
              onClick={handleComplete}
              disabled={!downloadedBackupCodes}
            >
              Complete Setup
            </Button>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enable Two-Factor Authentication</DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>

      <DialogActions>{getActionButtons()}</DialogActions>
    </Dialog>
  );
};
